import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import { defaultCreditScoreUrl } from 'embed/constants/config'
import {
  Application,
  CoApplicantFormState,
  Field,
  FieldDataValue,
  isCreditCardDebtCompany,
  PlaidStatus,
  PublicClassNames,
  Waypoints,
} from 'embed/constants'
import { Fields } from 'embed/constants/fields'

import AdvertiserDisclosure from 'embed/components/AdvertiserDisclosure'
import { ApplicationField } from 'embed/components/ApplicationField/container'
import ApplicationSubmitButton from 'embed/components/ApplicationSubmitButton/container'
import ConnectPlaid from 'embed/components/ConnectPlaid'
import ThemeConsumer from 'embed/components/ThemeConsumer'

import { NoAffect } from 'embed/components/Application/NoAffect'
import { ReturnToSearch } from 'embed/components/Application/ReturnToSearch'
import { TCPALanguage } from 'embed/components/Application/TCPALanguage'
import PlaidStatusIndicator from 'embed/components/ConnectPlaid/StatusIndicator'

import { GoogleAnalyticsActions, LoanPurposeOptions, LoanStep, Product, VehicleIdTypeOptions } from 'even-ts-static'
import FormSectionToggle from '../../FormSectionToggle'
import Waypoint from '../../Waypoint'
import Header from './Header'
const styles = require('embed/styles/application')
const InfoIcon = require('./info-blue.svg')
import { handleVehicleIdTypeChange } from './Stepbystep/AutomobileRefinance/automobile-refinance-util'

interface OwnProps {
  application: Application
  coApplicantAddressOnChange?: (field: Field, value: string) => void
  consent: boolean
  consentValidationStatus: boolean
  companyUuid: string
  creditScoreOfferUrl: string | undefined
  directReferral: boolean
  dispatchBackToStepOne: () => void
  featureFlags?: string[]
  handleConsentsToFCRAandTCPA: () => void
  handleVehicleIdTypeChangeHandler: (fields: Field[], value: VehicleIdTypeOptions) => void
  hasCoApplicant: boolean
  loanPurpose: FieldDataValue
  loanPurposeOnChange?: (value: FieldDataValue) => void
  onChange: (field: Field, value) => void
  onCoApplicantToggleClick: (value: string) => void
  onConsentChange: (event) => void
  plaidStatus: PlaidStatus
  selectedPlaidIntegrationVariantId?: string
  shouldAllowLoanCoApplicant: boolean
  showCoApplicantAddressFields: boolean
  hideLoanPurpose: boolean
}

type Props = OwnProps

const BUTTON_TEXT = 'Agree and Continue'

export default class LoanApplicationView extends React.Component<Props> {
  constructor(props) {
    super(props)
  }
  render() {
    const { handleVehicleIdTypeChangeHandler } = this.props
    return (
      <ThemeConsumer>
        {({ backdropStyles, borderRadiusLarge, boxStyles, colorBorder, colorPrimary, fontFamilyPrimary }) => (
          <React.Fragment>
            <Header />
            <div
              className={classnames(
                PublicClassNames.ApplicationContainer,
                styles.container,
                css`
                  background-color: ${backdropStyles.backgroundColor};
                  border-color: ${colorBorder};
                  border-radius: ${borderRadiusLarge};
                `,
              )}
              style={boxStyles}
            >
              {!this.props.directReferral && (
                <ReturnToSearch className={styles.backToSearch} onClick={this.props.dispatchBackToStepOne} />
              )}
              <NoAffect />
              {this.props.plaidStatus === PlaidStatus.Loading && <PlaidStatusIndicator />}
              {this.props.plaidStatus !== PlaidStatus.Loading && (
                <React.Fragment>
                  <section className={styles.formSection}>
                    <div className={styles.advertiserDisclosure}>
                      <AdvertiserDisclosure />
                    </div>
                    <h2
                      className={classnames(
                        styles.heading,
                        css`
                          font-family: ${fontFamilyPrimary};
                        `,
                      )}
                    >
                      Loan Info
                    </h2>
                    <ApplicationField
                      product={Product.Loan}
                      field={Fields.LoanAmount}
                      styles={css`
                        width: calc(33% - (1rem * 2));
                      `}
                    />
                    <ApplicationField
                      field={Fields.LoanPurpose}
                      hidden={this.props.hideLoanPurpose}
                      onChange={this.props.loanPurposeOnChange}
                      product={Product.Loan}
                      styles={css`
                        width: calc(33% - (1rem * 2));
                      `}
                    />
                    <ApplicationField
                      product={Product.Loan}
                      field={Fields.CreditRating}
                      styles={css`
                        width: calc(33% - (1rem * 2));
                      `}
                    >
                      <div className={styles.creditScore}>
                        <a
                          data-test-id="app_creditScoreLink"
                          className={css`
                            color: ${colorPrimary};
                          `}
                          href={this.props.creditScoreOfferUrl ?? defaultCreditScoreUrl}
                          target="_blank"
                        >
                          <span>Don't know? Click here to get your score.</span>
                        </a>
                      </div>
                    </ApplicationField>
                    {this.props.companyUuid === '5eeab848-2ab7-43b0-aa78-879868f0a327' &&
                      this.props.plaidStatus === PlaidStatus.NotConnected && (
                        <div className={styles.connectPlaidContainer}>
                          <ConnectPlaid />
                          <div className={styles.enterManaullyDesktop}>OR enter your information manually below.</div>
                          <div className={styles.enterManaullyMobile}>
                            <div className={styles.dividerContainer}>
                              <div className={styles.divider} />
                              <div className={styles.dividerOr}>OR</div>
                              <div className={styles.divider} />
                            </div>
                            <div>Enter your information manually</div>
                          </div>
                        </div>
                      )}
                    <Waypoint name={Waypoints.LoanInfo} />
                  </section>
                  <form>
                    <section className={styles.formSection}>
                      <h2
                        className={classnames(
                          styles.heading,
                          css`
                            font-family: ${fontFamilyPrimary};
                          `,
                        )}
                      >
                        Your Info
                      </h2>
                      <PlaidStatusIndicator />
                      <ApplicationField product={Product.Loan} field={Fields.EmailAddress} />
                      <ApplicationField product={Product.Loan} field={Fields.DateOfBirth} />
                      <ApplicationField product={Product.Loan} field={Fields.FirstName} />
                      <ApplicationField product={Product.Loan} field={Fields.LastName} />
                      <ApplicationField product={Product.Loan} field={Fields.PropertyStatus} />
                      <ApplicationField product={Product.Loan} field={Fields.Education} />
                      {isCreditCardDebtCompany(this.props.companyUuid) && (
                        <ApplicationField product={Product.Loan} field={Fields.CreditCardDebt} />
                      )}
                      <Waypoint name={Waypoints.YourInfo} />
                    </section>
                    {this.props.loanPurpose === LoanPurposeOptions.AUTO_REFINANCE && (
                      <section className={styles.formSection}>
                        <h2
                          className={classnames(
                            styles.heading,
                            css`
                              font-family: ${fontFamilyPrimary};
                            `,
                          )}
                        >
                          Vehicle Info
                        </h2>
                        <ApplicationField
                          styles={css`
                            width: calc(33% - (1rem * 2));
                          `}
                          product={Product.Loan}
                          field={Fields.AutomobileLoanBalance}
                        />
                        <ApplicationField
                          styles={css`
                            width: calc(33% - (1rem * 2));
                          `}
                          product={Product.Loan}
                          field={Fields.MonthlyPayment}
                        />
                        <ApplicationField
                          styles={css`
                            width: calc(33% - (1rem * 2));
                          `}
                          product={Product.Loan}
                          field={Fields.EstimatedMileage}
                        />
                        <ApplicationField
                          styles={css`
                            width: calc(33% - (1rem * 2));
                          `}
                          onChange={handleVehicleIdTypeChange(handleVehicleIdTypeChangeHandler)}
                          product={Product.Loan}
                          field={Fields.VehicleIdType}
                        />
                        {this.props.application.fields?.vehicleIdType?.value && (
                          <React.Fragment>
                            {this.props.application.fields.vehicleIdType.value ===
                              VehicleIdTypeOptions.VEHICLE_IDENTIFICATION_NUMBER && (
                              <ApplicationField
                                styles={css`
                                  width: calc(33% - (1rem * 2));
                                `}
                                product={Product.Loan}
                                field={Fields.VehicleIdentificationNumber}
                              />
                            )}
                            {this.props.application.fields.vehicleIdType.value ===
                              VehicleIdTypeOptions.LICENSE_PLATE && (
                              <React.Fragment>
                                <ApplicationField
                                  styles={css`
                                    width: calc(33% - (1rem * 2));
                                  `}
                                  product={Product.Loan}
                                  field={Fields.LicensePlateNumber}
                                />
                                <ApplicationField
                                  styles={css`
                                    width: calc(33% - (1rem * 2));
                                  `}
                                  product={Product.Loan}
                                  field={Fields.LicensePlateState}
                                />
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        )}
                      </section>
                    )}
                    <section className={styles.formSection}>
                      <h2
                        className={classnames(
                          styles.heading,
                          css`
                            font-family: ${fontFamilyPrimary};
                          `,
                        )}
                      >
                        Contact Info
                      </h2>
                      <ApplicationField product={Product.Loan} field={Fields.AddressOne} />
                      <ApplicationField product={Product.Loan} field={Fields.AddressTwo} />
                      <ApplicationField product={Product.Loan} field={Fields.Zipcode} />
                      <ApplicationField
                        product={Product.Loan}
                        field={Fields.City}
                        styles={css`
                          width: calc(30% - (1rem * 2));
                        `}
                      />
                      <ApplicationField
                        product={Product.Loan}
                        field={Fields.State}
                        styles={css`
                          width: calc(20% - (1rem * 2));
                        `}
                      />
                      <ApplicationField product={Product.Loan} field={Fields.PhoneNumber} />
                      <Waypoint name={Waypoints.ContactInfo} />
                    </section>
                    {this.props.shouldAllowLoanCoApplicant &&
                      this.props.application.fields?.purpose &&
                      this.props.application.fields.purpose.value !== LoanPurposeOptions.STUDENT_REFINANCE && (
                        <section>
                          <FormSectionToggle
                            onClick={this.props.onCoApplicantToggleClick}
                            showButtonText="Add Co-Applicant"
                            hideButtonText="Remove Co-Applicant"
                            showFormSection={this.props.hasCoApplicant}
                            tooltip={
                              'A co-applicant is an additional person considered in the underwriting and approval of a loan who shares legal responsibility for the loan. Applying for a loan with a co-applicant may help to improve the chances of a loan approval and may provide for more favorable loan terms.'
                            }
                            trackingEvent={{
                              action: GoogleAnalyticsActions.ToggledCoapplicant,
                              hidden: CoApplicantFormState.HIDDEN,
                              shown: CoApplicantFormState.SHOWN,
                            }}
                          />
                        </section>
                      )}
                    {this.props.hasCoApplicant && (
                      <section className={styles.formSection}>
                        <h2
                          className={classnames(
                            styles.heading,
                            css`
                              font-family: ${fontFamilyPrimary};
                            `,
                          )}
                        >
                          Co-Applicant Info
                        </h2>
                        <ApplicationField product={Product.Loan} field={Fields.CoApplicantFirstName} />
                        <ApplicationField product={Product.Loan} field={Fields.CoApplicantLastName} />
                        <ApplicationField product={Product.Loan} field={Fields.CoApplicantDateOfBirth} />
                        <ApplicationField product={Product.Loan} field={Fields.CoApplicantAnnualIncomeLoan} />
                        <ApplicationField
                          product={Product.Loan}
                          onChange={(event: string) =>
                            this.props.coApplicantAddressOnChange?.(Fields.CoApplicantAddressMatch, event)
                          }
                          field={Fields.CoApplicantAddressMatch}
                          styles={css`
                            width: 100%;
                          `}
                        />
                        {this.props.showCoApplicantAddressFields && (
                          <section
                            className={classnames(
                              styles.formSection,
                              css`
                                width: 100%;
                              `,
                            )}
                          >
                            <ApplicationField product={Product.Loan} field={Fields.CoApplicantAddressOne} />
                            <ApplicationField product={Product.Loan} field={Fields.CoApplicantAddressTwo} />
                            <ApplicationField product={Product.Loan} field={Fields.CoApplicantZipcode} />
                            <ApplicationField
                              product={Product.Loan}
                              field={Fields.CoApplicantCity}
                              styles={css`
                                width: calc(50% - (1rem * 2));
                              `}
                            />
                            <ApplicationField
                              product={Product.Loan}
                              field={Fields.CoApplicantState}
                              styles={css`
                                width: calc(50% - (1rem * 2));
                              `}
                            />

                            <Waypoint name={Waypoints.CoApplicantAddress} />
                          </section>
                        )}
                        <Waypoint name={Waypoints.CoApplicantInfo} />
                      </section>
                    )}
                  </form>
                  <section className={styles.formSection}>
                    <h2
                      className={classnames(
                        styles.heading,
                        css`
                          font-family: ${fontFamilyPrimary};
                        `,
                      )}
                    >
                      Employment Info
                    </h2>
                    <ApplicationField product={Product.Loan} field={Fields.EmploymentStatus} />
                    <ApplicationField product={Product.Loan} field={Fields.AnnualIncomeLoan} />
                    <ApplicationField product={Product.Loan} field={Fields.PayFrequency} />
                    <ApplicationField product={Product.Loan} field={Fields.SocialSecurityNumber}>
                      <div
                        className={classnames(
                          styles.creditScore,
                          css`
                            color: ${colorPrimary};
                            svg path {
                              fill: ${colorPrimary};
                            }
                          `,
                        )}
                      >
                        <InfoIcon />
                        <em
                          className={css`
                            margin: auto 0px;
                          `}
                        >
                          This will NOT affect your credit score
                        </em>
                      </div>
                    </ApplicationField>
                    <Waypoint name={Waypoints.EmploymentInfo} />
                  </section>

                  <Waypoint name={Waypoints.SubmitBtn} />
                  <ApplicationSubmitButton
                    product={Product.Loan}
                    nextStep={LoanStep.Results}
                    handleBeforeSubmit={this.props.handleConsentsToFCRAandTCPA}
                  >
                    <span className={styles.consentButtonLabel}>{BUTTON_TEXT}</span>
                  </ApplicationSubmitButton>
                  <TCPALanguage buttonText={BUTTON_TEXT} className={styles.consentWithButton} product={Product.Loan} />
                </React.Fragment>
              )}
            </div>
          </React.Fragment>
        )}
      </ThemeConsumer>
    )
  }
}
